/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  AmountJson,
  AmountString,
  Amounts,
  HttpStatusCode,
  TalerMerchantApi,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  RenderAmountBulma,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { format } from "date-fns";
import { ComponentChildren, Fragment, VNode, h } from "preact";
import { StateUpdater, useState } from "preact/hooks";
import emptyImage from "../../../../assets/empty.png";
import {
  FormErrors,
  FormProvider,
  TalerForm,
} from "../../../../components/form/FormProvider.js";
import { InputCurrency } from "../../../../components/form/InputCurrency.js";
import { InputNumber } from "../../../../components/form/InputNumber.js";
import { FragmentPersonaFlag } from "../../../../components/menu/SideBar.js";
import { useSessionContext } from "../../../../context/session.js";
import { WithId } from "../../../../declaration.js";
import {
  UIElement,
  dateFormatForPreferences,
  usePreference,
} from "../../../../hooks/preference.js";
import { Tooltip } from "../../../../components/Tooltip.js";

const TALER_SCREEN_ID = 56;

type Entity = TalerMerchantApi.ProductDetailResponse & WithId;

interface Props {
  instances: Entity[];
  onDelete?: (id: Entity) => void;
  onSelect: (product: Entity) => void;
  onCreate?: () => void;
  selected?: boolean;
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
}

export function CardTable({
  instances,
  onCreate,
  onSelect,
  onDelete,
  onLoadMoreAfter,
  onLoadMoreBefore,
}: Props): VNode {
  const [rowSelection, rowSelectionHandler] = useState<string | undefined>(
    undefined,
  );
  const { i18n } = useTranslationContext();
  return (
    <div class="card has-table">
      <header class="card-header">
        <p class="card-header-title">
          <i class="icon mdi mdi-shopping" />
          <i18n.Translate>Inventory</i18n.Translate>
        </p>
        <div class="card-header-icon" aria-label="more options">
          {!onCreate ? undefined : (
            <Tooltip text={i18n.str`Add product to inventory`}>
              <button
                class="button is-info"
                type="button"
                onClick={onCreate}
                accessKey="+"
              >
                <i class="icon mdi mdi-plus mdi-36px" />
              </button>
            </Tooltip>
          )}
        </div>
      </header>
      <div class="card-content">
        <div class="b-table has-pagination">
          <div class="table-wrapper has-mobile-cards">
            {instances.length > 0 ? (
              <Table
                instances={instances}
                onSelect={onSelect}
                onDelete={onDelete}
                onLoadMoreAfter={onLoadMoreAfter}
                onLoadMoreBefore={onLoadMoreBefore}
                rowSelection={rowSelection}
                rowSelectionHandler={rowSelectionHandler}
              />
            ) : (
              <EmptyTable />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
interface TableProps {
  rowSelection: string | undefined;
  instances: Entity[];
  onSelect: (id: Entity) => void;
  onDelete: ((id: Entity) => void) | undefined;
  rowSelectionHandler: StateUpdater<string | undefined>;
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
}

function Table({
  rowSelection,
  rowSelectionHandler,
  instances,
  onSelect,
  onDelete,
  onLoadMoreAfter,
  onLoadMoreBefore,
}: TableProps): VNode {
  const { i18n } = useTranslationContext();
  const [preference] = usePreference();
  const { state: session, lib, config } = useSessionContext();
  const [notification, safeFunctionHandler] = useLocalNotificationBetter();
  const update = safeFunctionHandler(
    i18n.str`update product`,
    lib.instance.updateProduct.bind(lib.instance),
  );
  update.onSuccess = () => rowSelectionHandler(undefined);
  update.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized.`;
      case HttpStatusCode.NotFound:
        return i18n.str`Product not found.`;
      case HttpStatusCode.Conflict:
        return i18n.str`This change was made without outdated info.`;
      default:
        assertUnreachable(fail);
    }
  };
  return (
    <div class="table-container">
      {onLoadMoreBefore && (
        <button class="button is-fullwidth" onClick={onLoadMoreBefore}>
          <i18n.Translate>Load first page</i18n.Translate>
        </button>
      )}
      <table class="table is-fullwidth is-striped is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>
              <i18n.Translate>Image</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Name</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Description</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Price per unit</i18n.Translate>
            </th>
            <FragmentPersonaFlag point={UIElement.option_inventoryTaxes}>
              <th>
                <i18n.Translate>Taxes</i18n.Translate>
              </th>
              <th>
                <i18n.Translate>Sales</i18n.Translate>
              </th>
            </FragmentPersonaFlag>
            <th>
              <i18n.Translate>Stock</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Sold</i18n.Translate>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {instances.map((i) => {
            const restStockInfo = !i.next_restock
              ? ""
              : i.next_restock.t_s === "never"
                ? i18n.str`Never`
                : i18n.str`restock at ${format(
                    new Date(i.next_restock.t_s * 1000),
                    dateFormatForPreferences(preference),
                  )}`;
            let stockInfo: ComponentChildren = "";
            if (i.total_stock < 0) {
              stockInfo = i18n.str`infinite`;
            } else {
              const totalStock = i.total_stock - i.total_lost - i.total_sold;
              stockInfo = (
                <label title={restStockInfo}>
                  {totalStock} {i.unit}
                </label>
              );
            }

            const isFree = Amounts.isZero(Amounts.parseOrThrow(i.price));

            return (
              <Fragment key={i.id}>
                <tr key="info">
                  <td
                    onClick={() =>
                      rowSelection !== i.id && rowSelectionHandler(i.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={i.image ? i.image : emptyImage}
                      style={{
                        border: "solid black 1px",
                        maxHeight: "2em",
                        width: "auto",
                        height: "auto",
                      }}
                    />
                  </td>
                  <td
                    onClick={() =>
                      rowSelection !== i.id && rowSelectionHandler(i.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {i.product_name}
                  </td>
                  <td
                    onClick={() =>
                      rowSelection !== i.id && rowSelectionHandler(i.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <Tooltip text={i.description as TranslatedString}>
                      {i.description.length > 30
                        ? i.description.substring(0, 30) + "..."
                        : i.description}
                    </Tooltip>
                  </td>
                  <td
                    onClick={() =>
                      rowSelection !== i.id && rowSelectionHandler(i.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {isFree ? (
                      i18n.str`Free`
                    ) : (
                      <Fragment>
                        <RenderAmountBulma
                          value={Amounts.parseOrThrow(i.price)}
                          specMap={config.currencies}
                        />{" "}
                        / {i.unit}
                      </Fragment>
                    )}
                  </td>
                  <FragmentPersonaFlag point={UIElement.option_inventoryTaxes}>
                    <td
                      onClick={() =>
                        rowSelection !== i.id && rowSelectionHandler(i.id)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <RenderAmountBulma
                        value={sum(
                          i.taxes,
                          Amounts.zeroOfCurrency(config.currency),
                        )}
                        specMap={config.currencies}
                      />
                    </td>
                    <td
                      onClick={() =>
                        rowSelection !== i.id && rowSelectionHandler(i.id)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <RenderAmountBulma
                        value={difference(
                          Amounts.parseOrThrow(i.price),
                          sum(i.taxes, Amounts.zeroOfCurrency(config.currency)),
                        )}
                        specMap={config.currencies}
                      />
                    </td>
                  </FragmentPersonaFlag>
                  <td
                    onClick={() =>
                      rowSelection !== i.id && rowSelectionHandler(i.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {stockInfo}
                  </td>
                  <td
                    onClick={() =>
                      rowSelection !== i.id && rowSelectionHandler(i.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <span style={{ whiteSpace: "nowrap" }}>
                      {i.total_sold} {i.unit}
                    </span>
                  </td>
                  <td class="is-actions-cell right-sticky">
                    <div class="buttons is-right">
                      <Tooltip text={i18n.str`Go to product update page`}>
                        <button
                          class="button is-small is-success "
                          type="button"
                          onClick={(): void => onSelect(i)}
                        >
                          <i18n.Translate>Update</i18n.Translate>
                        </button>
                      </Tooltip>
                      {!onDelete ? undefined : (
                        <Tooltip
                          text={i18n.str`Remove this product from the database`}
                        >
                          <button
                            class="button is-small is-danger"
                            type="button"
                            onClick={(): void => onDelete(i)}
                          >
                            <i18n.Translate>Delete</i18n.Translate>
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
                {rowSelection === i.id && (
                  <tr key="form">
                    <td colSpan={10}>
                      <FastProductUpdateForm
                        product={i}
                        onUpdate={async (prod) => {
                          (!session.token
                            ? update
                            : update.withArgs(session.token, i.id, prod)
                          ).call();
                        }}
                        onCancel={() => rowSelectionHandler(undefined)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {onLoadMoreAfter && (
        <Tooltip text={i18n.str`Load more products after the last one`}>
          <button
            type="button"
            class="button is-fullwidth"
            onClick={onLoadMoreAfter}
          >
            <i18n.Translate>Load next page</i18n.Translate>
          </button>
        </Tooltip>
      )}
    </div>
  );
}

interface FastProductUpdateFormProps {
  product: Entity;
  onUpdate: (data: TalerMerchantApi.ProductPatchDetailRequest) => Promise<void>;
  onCancel: () => void;
}
interface FastProductUpdate extends TalerForm {
  incoming: number;
  lost: number;
  price: string;
}
interface UpdatePrice {
  price: string;
}

function FastProductWithInfiniteStockUpdateForm({
  product,
  onUpdate,
  onCancel,
}: FastProductUpdateFormProps) {
  const [value, valueHandler] = useState<UpdatePrice>({ price: product.price });
  const { i18n } = useTranslationContext();

  return (
    <Fragment>
      <FormProvider<FastProductUpdate>
        name="added"
        object={value}
        valueHandler={valueHandler as any}
      >
        <InputCurrency<FastProductUpdate>
          name="price"
          label={i18n.str`Price per unit`}
          tooltip={i18n.str`Update the product with new price`}
        />
      </FormProvider>

      <div class="buttons is-expanded">
        <div class="buttons is-right mt-5">
          <button type="button" class="button" onClick={onCancel}>
            <i18n.Translate>Cancel</i18n.Translate>
          </button>
          <Tooltip text={i18n.str`Update product with new price`}>
            <button
              type="button"
              class="button is-info"
              onClick={() =>
                onUpdate({
                  ...product,
                  price: value.price as AmountString,
                })
              }
            >
              <i18n.Translate>Confirm update</i18n.Translate>
            </button>
          </Tooltip>
        </div>
      </div>
    </Fragment>
  );
}

function FastProductWithManagedStockUpdateForm({
  product,
  onUpdate,
  onCancel,
}: FastProductUpdateFormProps) {
  const [value, valueHandler] = useState<FastProductUpdate>({
    incoming: 0,
    lost: 0,
    price: product.price,
  });
  const { i18n } = useTranslationContext();

  const currentStock =
    product.total_stock - product.total_sold - product.total_lost;

  const errors: FormErrors<FastProductUpdate> = {
    lost:
      currentStock + value.incoming < value.lost
        ? i18n.str`lost can't be greater that current + incoming (max ${
            currentStock + value.incoming
          })`
        : undefined,
  };

  const hasErrors = Object.keys(errors).some(
    (k) => (errors as Record<string, unknown>)[k] !== undefined,
  );

  return (
    <Fragment>
      <FormProvider<FastProductUpdate>
        name="added"
        errors={errors}
        object={value}
        valueHandler={valueHandler as any}
      >
        <InputNumber<FastProductUpdate>
          name="incoming"
          label={i18n.str`Incoming`}
          tooltip={i18n.str`Add more elements to the inventory`}
        />
        <InputNumber<FastProductUpdate>
          name="lost"
          label={i18n.str`Lost`}
          tooltip={i18n.str`Report elements lost in the inventory`}
        />
        <InputCurrency<FastProductUpdate>
          name="price"
          label={i18n.str`Price per unit`}
          tooltip={i18n.str`New price for the product`}
        />
      </FormProvider>

      <div class="buttons is-right mt-5">
        <button type="button" class="button" onClick={onCancel}>
          <i18n.Translate>Cancel</i18n.Translate>
        </button>
        <Tooltip
          text={
            hasErrors
              ? i18n.str`There are values with errors.`
              : i18n.str`Update product with new stock and price`
          }
        >
          <button
            type="button"
            class="button is-info"
            disabled={hasErrors}
            onClick={() =>
              onUpdate({
                ...product,
                total_stock: product.total_stock + value.incoming,
                total_lost: product.total_lost + value.lost,
                price: value.price as AmountString,
              })
            }
          >
            <i18n.Translate>Confirm</i18n.Translate>
          </button>
        </Tooltip>
      </div>
    </Fragment>
  );
}

function FastProductUpdateForm(props: FastProductUpdateFormProps) {
  return props.product.total_stock === -1 ? (
    <FastProductWithInfiniteStockUpdateForm {...props} />
  ) : (
    <FastProductWithManagedStockUpdateForm {...props} />
  );
}

function EmptyTable(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="content has-text-grey has-text-centered">
      <p>
        <span class="icon is-large">
          <i class="icon mdi mdi-magnify mdi-48px" />
        </span>
      </p>
      <p>
        <i18n.Translate>
          There are no products to list yet, add more by pressing the + sign
        </i18n.Translate>
      </p>
    </div>
  );
}

function difference(price: AmountJson, tax: AmountJson): AmountJson {
  if (!tax) return price;
  return Amounts.sub(price, tax).amount;
}
function sum(
  taxes: TalerMerchantApi.Tax[] | undefined,
  zero: AmountJson,
): AmountJson {
  if (taxes === undefined) return zero;
  return taxes.reduce((p, c) => {
    return Amounts.add(p, c.tax).amount;
  }, zero);
}
